// Three states, never two: a Registrar that never registered a machine client
// is not the same as one whose client is suspended or revoked, and the list and
// the entity file must not disagree about which is which.
import { useTranslate } from '@tolgee/react';
import { MudTag } from '@egov-moldova/mud-react';
import { BlockTags } from '../blocking/BlockTags';
import type { BlockState } from '../blocking/block-status';
import type { SystemCredentialState } from './api';

// The registers that draw this tag are the System Administrator's, so a block is
// named by who set it (CF-48, TC-UC21-08-03).
const LABELS: Record<BlockState, string> = {
  active: 'registrars.system.active',
  inactive: 'registrars.system.suspended',
  blocked_by_system_admin: 'registrars.system.blocked_by_system_admin',
  blocked_by_registrar: 'registrars.system.blocked_by_registrar',
};

export interface CredentialTagProps extends SystemCredentialState {
  blockedBySystemAdmin?: boolean;
  blockedByRegistrar?: boolean;
}

export function CredentialTag({
  configured,
  active,
  blockedBySystemAdmin = false,
  blockedByRegistrar = false,
}: CredentialTagProps): JSX.Element {
  const { t } = useTranslate();
  // The wireframe paints "never configured" in the brand accent, not grey: it is a
  // state to act on, and grey-on-grey made it disappear against the zebra rows
  // (wireframes/react/src/components/TagSistem.tsx:3).
  // AGE's `accent` is a burnt orange (rgb(181,71,8), measured); the wireframe's
  // `.tag.accent` is brand blue (#0058d2). AGE calls that one `brand`. The two
  // systems use the same word for different colours, which is how every one of
  // these tags came out amber where the design says blue.
  // Small, as the wireframe's `.tag.sm` draws it wherever the component appears.
  if (!configured) {
    return (
      <MudTag variant="status" size="sm" semantic="brand" label={t('registrars.system.unset')} />
    );
  }
  return (
    <BlockTags
      item={{ active, blockedBySystemAdmin, blockedByRegistrar }}
      viewer="SYSTEM_ADMIN"
      labels={LABELS}
      size="sm"
    />
  );
}
